import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Plus, Trash2 } from "lucide-react";
import { api, messageErreur } from "../api";
import { useApp } from "../App";
import { AdresseAutocomplete, Btn, Champ, Micro } from "../components/Bits";
import { Marque } from "../components/Shell";
import {
  HORAIRES_DEFAUT, METIERS, PRESTATIONS_TYPES, euros, joursSemaine,
} from "../lib/format";

const ETAPES = ["Métier", "Prestations", "Horaires", "Identité"];

function slugifier(v) {
  return (v || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase()
    .slice(0, 40);
}

export default function Onboarding() {
  const nav = useNavigate();
  const { compte, setCompte } = useApp();
  const [etape, setEtape] = useState(0);
  const [envoi, setEnvoi] = useState(false);

  const [metier, setMetier] = useState(compte?.metier && compte.metier !== "autre" ? compte.metier : "");
  const [activite, setActivite] = useState(compte?.activite || "");
  const [prestations, setPrestations] = useState([]);
  const [horaires, setHoraires] = useState(HORAIRES_DEFAUT);
  const [telephone, setTelephone] = useState(compte?.telephone || "");
  const [adresse, setAdresse] = useState("");
  const [instagram, setInstagram] = useState("");
  const [slug, setSlug] = useState(slugifier(compte?.activite || ""));

  const choisirMetier = (code) => {
    setMetier(code);
    setPrestations((PRESTATIONS_TYPES[code] || PRESTATIONS_TYPES.autre).map((p) => ({ ...p })));
  };

  const majPresta = (i, champ, valeur) =>
    setPrestations((l) => l.map((p, j) => (j === i ? { ...p, [champ]: valeur } : p)));

  const jourOuvert = (j) => Array.isArray(horaires[j]) && horaires[j].length > 0;

  const basculerJour = (j) =>
    setHoraires((h) => ({
      ...h,
      [j]: jourOuvert(j) ? [] : [{ start: "09:00", end: "18:00" }],
    }));

  const majPlage = (j, champ, valeur) =>
    setHoraires((h) => ({ ...h, [j]: [{ ...(h[j]?.[0] || { start: "09:00", end: "18:00" }), [champ]: valeur }] }));

  const peutContinuer = useMemo(() => {
    if (etape === 0) return !!metier && activite.trim().length > 1;
    if (etape === 1)
      return (
        prestations.length > 0 &&
        prestations.every((p) => p.nom?.trim() && Number(p.duree_min) >= 5 && Number(p.prix) >= 0)
      );
    if (etape === 2) return Object.keys(horaires).some((j) => jourOuvert(j));
    return slug.length >= 2;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etape, metier, activite, prestations, horaires, slug]);

  const terminer = async () => {
    setEnvoi(true);
    try {
      const weekly = {};
      Object.entries(horaires).forEach(([j, plages]) => {
        if (plages && plages.length) weekly[String(j)] = plages;
      });
      const { data } = await api.post("/onboarding", {
        metier,
        activite: activite.trim(),
        prenom: compte?.prenom,
        telephone: telephone.trim() || null,
        adresse: adresse.trim() || null,
        instagram: instagram.trim() || null,
        slug,
        prestations: prestations.map((p) => ({
          nom: p.nom.trim(),
          duree_min: Number(p.duree_min),
          buffer_min: Number(p.buffer_min || 0),
          prix: Number(p.prix),
          actif: true,
        })),
        weekly,
      });
      setCompte(data.compte);
      toast.success("Tout est prêt. Bienvenue.");
      nav("/journee", { replace: true });
    } catch (e) {
      toast.error(messageErreur(e, "La configuration n'a pas pu être enregistrée."));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--paper)] flex flex-col">
      <header className="border-b border-[var(--rule)]">
        <div className="colonne pad h-14 flex items-center justify-between">
          <Marque lien={false} />
          <Micro muted>Étape {etape + 1} sur 4</Micro>
        </div>
      </header>

      {/* Progression : quatre filets, pas une barre de chargement. */}
      <div className="colonne pad pt-6">
        <div className="flex gap-1.5">
          {ETAPES.map((e, i) => (
            <div key={e} className="flex-1">
              <div
                className="h-[3px]"
                style={{ background: i <= etape ? "var(--accent)" : "var(--rule)" }}
              />
              <div
                className={`text-[10px] font-semibold uppercase tracking-[0.13em] mt-2 ${
                  i <= etape ? "text-[var(--accent)]" : "text-[var(--ink-muted)]"
                }`}
              >
                {e}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="colonne pad flex-1 py-8 max-w-[720px]">
        {etape === 0 && (
          <div className="entree">
            <h1 className="display text-[30px] md:text-[36px]">C'est quoi ton métier ?</h1>
            <p className="text-[15px] text-[var(--ink-soft)] mt-3">
              Servae préremplit tes prestations, tes durées et tes prix habituels. Tu
              corrigerez ensuite — c'est plus rapide que de tout saisir.
            </p>
            <div className="grid sm:grid-cols-2 gap-px bg-[var(--rule)] border border-[var(--rule)] mt-7">
              {METIERS.map((m) => (
                <button
                  key={m.code}
                  onClick={() => choisirMetier(m.code)}
                  data-testid={`metier-${m.code}`}
                  className={`text-left px-4 py-4 ${
                    metier === m.code
                      ? "bg-[var(--accent-tint)] text-[var(--ink)]"
                      : "bg-[var(--surface)] hover:bg-[#fffdfb]"
                  }`}
                >
                  <span className="text-[15px] font-semibold">{m.label}</span>
                </button>
              ))}
            </div>
            <Champ
              label="Nom de ton activité"
              className="mt-7"
              value={activite}
              onChange={(e) => {
                setActivite(e.target.value);
                setSlug(slugifier(e.target.value));
              }}
              placeholder="Studio Onyx"
              indice="Tes clientes le verront dans les rappels et sur ta page de réservation"
              data-testid="champ-activite"
            />
          </div>
        )}

        {etape === 1 && (
          <div className="entree">
            <h1 className="display text-[30px] md:text-[36px]">Tes prestations</h1>
            <p className="text-[15px] text-[var(--ink-soft)] mt-3">
              Le battement est le temps de remise en état après la prestation. Servae ne
              proposera jamais un créneau qui empiète dessus.
            </p>
            <div className="mt-7 space-y-3">
              {prestations.map((p, i) => (
                <div key={i} className="surface rounded-[4px] p-4" data-testid={`presta-${i}`}>
                  <div className="flex items-start gap-3">
                    <Champ
                      className="flex-1"
                      label="Prestation"
                      value={p.nom}
                      onChange={(e) => majPresta(i, "nom", e.target.value)}
                      data-testid={`presta-nom-${i}`}
                    />
                    <button
                      onClick={() => setPrestations((l) => l.filter((_, j) => j !== i))}
                      aria-label="Retirer"
                      data-testid={`presta-retirer-${i}`}
                      className="mt-7 h-11 w-11 inline-flex items-center justify-center rounded-[4px] text-[var(--text-subtle)] hover:text-[var(--danger)] hover:bg-[var(--surface-sunken)]"
                    >
                      <Trash2 size={16} strokeWidth={1.6} />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <Champ
                      label="Durée"
                      type="number"
                      inputMode="numeric"
                      value={p.duree_min}
                      onChange={(e) => majPresta(i, "duree_min", e.target.value)}
                      suffixe="min"
                      data-testid={`presta-duree-${i}`}
                    />
                    <Champ
                      label="Battement"
                      type="number"
                      inputMode="numeric"
                      value={p.buffer_min}
                      onChange={(e) => majPresta(i, "buffer_min", e.target.value)}
                      suffixe="min"
                      data-testid={`presta-buffer-${i}`}
                    />
                    <Champ
                      label="Prix"
                      type="number"
                      inputMode="decimal"
                      value={p.prix}
                      onChange={(e) => majPresta(i, "prix", e.target.value)}
                      suffixe="€"
                      data-testid={`presta-prix-${i}`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <Btn
              variante="fantome"
              className="mt-4"
              icone={Plus}
              onClick={() =>
                setPrestations((l) => [...l, { nom: "", duree_min: 60, buffer_min: 10, prix: 50 }])
              }
              data-testid="btn-ajouter-presta"
            >
              Ajouter une prestation
            </Btn>
          </div>
        )}

        {etape === 2 && (
          <div className="entree">
            <h1 className="display text-[30px] md:text-[36px]">Tes horaires habituels</h1>
            <p className="text-[15px] text-[var(--ink-soft)] mt-3">
              Tu pourras fermer un jour précis ou poser des congés plus tard, sans toucher à
              cette base.
            </p>
            <div className="mt-7 border border-[var(--rule)] bg-[var(--surface)]">
              {joursSemaine.map((nom, j) => (
                <div
                  key={j}
                  className="flex items-center gap-3 px-4 py-3 border-b border-[var(--rule)] last:border-b-0"
                >
                  <button
                    onClick={() => basculerJour(j)}
                    data-testid={`jour-${j}`}
                    className={`w-[104px] shrink-0 text-left text-[15px] font-semibold ${
                      jourOuvert(j) ? "text-[var(--ink)]" : "text-[var(--ink-muted)]"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ background: jourOuvert(j) ? "var(--accent)" : "var(--rule-strong)" }}
                      />
                      {nom}
                    </span>
                  </button>
                  {jourOuvert(j) ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="time"
                        value={horaires[j][0].start}
                        onChange={(e) => majPlage(j, "start", e.target.value)}
                        data-testid={`debut-${j}`}
                        className="h-10 px-2 num text-[15px] bg-[var(--surface)] border border-[var(--rule)] rounded-[4px] outline-none focus:border-[rgba(194,65,12,0.6)]"
                      />
                      <span className="text-[var(--ink-muted)]">→</span>
                      <input
                        type="time"
                        value={horaires[j][0].end}
                        onChange={(e) => majPlage(j, "end", e.target.value)}
                        data-testid={`fin-${j}`}
                        className="h-10 px-2 num text-[15px] bg-[var(--surface)] border border-[var(--rule)] rounded-[4px] outline-none focus:border-[rgba(194,65,12,0.6)]"
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => basculerJour(j)}
                      className="text-[13px] text-[var(--ink-muted)] flex-1 text-left"
                    >
                      Fermé — appuyez pour ouvrir
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {etape === 3 && (
          <div className="entree">
            <h1 className="display text-[30px] md:text-[36px]">Ta page de réservation</h1>
            <p className="text-[15px] text-[var(--ink-soft)] mt-3">
              Un lien à mettre dans ta bio Instagram. Tes clientes y voient tes vraies
              disponibilités et réservent seuls.
            </p>
            <div className="surface rounded-[4px] p-4 mt-6">
              <Micro>Ton adresse</Micro>
              <div className="mt-2 text-[14px] break-all">
                <span className="text-[var(--ink-muted)]">
                  {window.location.origin}/r/
                </span>
                <span className="font-semibold" data-testid="apercu-slug">{slug || "…"}</span>
              </div>
            </div>
            <div className="space-y-4 mt-5">
              <Champ
                label="Identifiant du lien"
                value={slug}
                onChange={(e) => setSlug(slugifier(e.target.value))}
                placeholder="studio-onyx"
                data-testid="champ-slug"
              />
              <Champ
                label="Ton mobile"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                placeholder="06 12 34 56 78"
                inputMode="tel"
                data-testid="champ-telephone"
              />
              <AdresseAutocomplete
                label="Adresse du studio (facultatif)"
                value={adresse}
                onChange={setAdresse}
                placeholder="12 rue des Lilas, 69003 Lyon"
                indice="Ajoutée automatiquement dans les rappels envoyés à tes clientes"
                testid="champ-adresse"
              />
              <Champ
                label="Instagram (facultatif)"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                placeholder="studio.onyx"
                data-testid="champ-instagram"
              />
            </div>
            <div className="surface rounded-[4px] p-4 mt-6">
              <Micro muted>Récapitulatif</Micro>
              <ul className="mt-3 space-y-1.5 text-[14px] text-[var(--ink-soft)]">
                <li>
                  {prestations.length} prestation{prestations.length > 1 ? "s" : ""} · de{" "}
                  {euros(Math.min(...prestations.map((p) => Number(p.prix) || 0)))} à{" "}
                  {euros(Math.max(...prestations.map((p) => Number(p.prix) || 0)))}
                </li>
                <li>
                  {Object.keys(horaires).filter((j) => jourOuvert(j)).length} jours d'ouverture par
                  semaine
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <div className="sticky bottom-0 bg-[var(--paper)] border-t border-[var(--rule)]">
        <div
          className="colonne pad py-4 flex items-center justify-between gap-3"
          style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom))" }}
        >
          <Btn
            variante="nu"
            icone={ArrowLeft}
            onClick={() => setEtape((e) => Math.max(0, e - 1))}
            disabled={etape === 0}
            data-testid="btn-precedent"
          >
            Retour
          </Btn>
          {etape < 3 ? (
            <Btn
              taille="lg"
              icone={ArrowRight}
              disabled={!peutContinuer}
              onClick={() => setEtape((e) => e + 1)}
              data-testid="btn-continuer"
            >
              Continuer
            </Btn>
          ) : (
            <Btn
              taille="lg"
              charge={envoi}
              disabled={!peutContinuer}
              onClick={terminer}
              data-testid="btn-terminer-onboarding"
            >
              Ouvrir mon agenda
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}
